"use client";

import { useEffect, useRef } from "react";

// Shared wiring for "run callback when the tab is visible; re-run on the
// visible edge; poll on an interval; stop when hidden." Used by
// service-worker-registration and PollFullNodeHealth. `enabled: false`
// short-circuits everything — for preconditions like "SW registration
// resolved" or "we already detected an update so stop polling."
// online/offline events are kept as hints to trigger an immediate callback
// but navigator.onLine is not read — the probe itself is the source of truth.
export function useVisibleAndOnlinePoll(
  callback: () => void,
  options: { intervalMs: number; enabled?: boolean },
): void {
  const { intervalMs, enabled = true } = options;

  // Hold the latest callback in a ref so identity churn doesn't tear down the
  // interval. Callers can pass inline arrow functions without paying for it.
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) return;

    let interval: ReturnType<typeof setInterval> | undefined;

    const shouldPoll = () => document.visibilityState === "visible";

    const run = () => callbackRef.current();

    const start = () => {
      if (interval !== undefined) return;
      interval = setInterval(run, intervalMs);
    };

    const stop = () => {
      if (interval !== undefined) {
        clearInterval(interval);
        interval = undefined;
      }
    };

    const sync = () => {
      if (shouldPoll()) {
        run();
        start();
      } else {
        stop();
      }
    };

    sync();
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("online", sync);
    window.addEventListener("offline", sync);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("online", sync);
      window.removeEventListener("offline", sync);
    };
  }, [intervalMs, enabled]);
}
